import { useEffect, useState, type FormEvent } from "react";
import type { LocationRow } from "@garden/types/db";
import { supabase } from "../auth";
import { createLocation } from "../db/locations";
import "./LocationCreateModal.css";

interface ContainerNameModalProps {
  gardenId: string;
  /** Centre point in viewport coordinates where the user clicked. */
  point: { x: number; y: number };
  onClose: () => void;
  onCreated: (loc: LocationRow) => void;
}

const CONTAINER_HALF = 12; // 24×24 box around the click point — matches seed sizing.

export function ContainerNameModal({ gardenId, point, onClose, onCreated }: ContainerNameModalProps) {
  const [name, setName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!supabase) {
      setError("Supabase not configured.");
      return;
    }
    if (!name.trim()) {
      setError("Name is required.");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const cx = Math.round(point.x);
      const cy = Math.round(point.y);
      const polygon: Array<[number, number]> = [
        [cx - CONTAINER_HALF, cy - CONTAINER_HALF],
        [cx + CONTAINER_HALF, cy - CONTAINER_HALF],
        [cx + CONTAINER_HALF, cy + CONTAINER_HALF],
        [cx - CONTAINER_HALF, cy + CONTAINER_HALF],
      ];
      const loc = await createLocation(supabase, {
        garden_id: gardenId,
        name: name.trim(),
        kind: "container",
        polygon,
        point: { x: cx, y: cy },
      });
      onCreated(loc);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not create container.");
      setSubmitting(false);
    }
  };

  return (
    <div className="Modal__backdrop" onClick={onClose} role="presentation">
      <div
        className="Modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="ctr-modal-title"
      >
        <h2 id="ctr-modal-title" className="Modal__title">
          New container
        </h2>
        <p className="Modal__subtitle">
          Placed at ({Math.round(point.x)}, {Math.round(point.y)}) in the garden.
        </p>

        <form onSubmit={handleSubmit} className="Modal__form">
          <label className="Modal__field">
            <span>Name</span>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              autoFocus
              placeholder="Tomato pot"
            />
          </label>

          {error && <p className="Modal__error">{error}</p>}

          <div className="Modal__actions">
            <button
              type="button"
              className="PlantForm__btn PlantForm__btn--ghost"
              onClick={onClose}
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="PlantForm__btn PlantForm__btn--primary"
              disabled={submitting}
            >
              {submitting ? "Creating…" : "Create container"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
